import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Floating-label input.
 *
 * Visual: tertiary chip (`bg-foreground/5`) with a 2px border that recolors
 * on focus or validation. The label sits inside the field at rest and
 * scales + lifts to a caption above the value once the user starts typing
 * or focuses the field. Optional `prefix` / `suffix` render inline next to
 * the input on the baseline (e.g. `https://` prefix or `.lndng.dev`
 * suffix).
 *
 * Used for the prominent forms — site creation, custom-domain add — where
 * the field IS the page rather than one of many. For dense forms use
 * `<Input>` instead.
 */
export interface FloatingInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'prefix' | 'suffix' | 'size'> {
  label: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  /**
   * Validation tone. 'success' / 'error' override the border color.
   */
  state?: 'default' | 'success' | 'error';
  /**
   * 'lg' (default) — text-xl, prominent forms where the field IS the page
   *   (custom-domain add).
   * 'md' — text-base, standard forms (site creation).
   */
  size?: 'lg' | 'md';
}

const FloatingInput = React.forwardRef<HTMLInputElement, FloatingInputProps>(
  ({ id, label, prefix, suffix, state = 'default', size = 'lg', className, ...props }, ref) => {
    const reactId = React.useId();
    const inputId = id ?? `floating-input-${reactId}`;

    const borderClass =
      state === 'error'
        ? 'border-destructive'
        : state === 'success'
          ? 'border-success'
          : '[&:focus-within]:border-foreground';

    // 'lg' is the prominent-form size used by Add Domain. 'md' is the
    // standard density used by Site creation.
    const isMd = size === 'md';
    const valueTextClass = isMd ? 'text-base' : 'text-xl';
    const rowPaddingClass = isMd ? 'px-3 pb-2 pt-5' : 'px-3 pb-2.5 pt-6';
    const labelTopClass = isMd ? 'top-3.5' : 'top-4';

    return (
      <div
        className={cn(
          'relative rounded-2xl border-2 border-transparent transition-colors',
          'bg-foreground/5',
          '[&:focus-within]:!bg-surface',
          borderClass,
          className,
        )}
      >
        <div className={cn('flex items-baseline', rowPaddingClass)}>
          {prefix !== undefined && (
            <span className={cn('text-muted-foreground whitespace-nowrap', valueTextClass)}>
              {prefix}
            </span>
          )}
          <input
            id={inputId}
            ref={ref}
            placeholder=" "
            className={cn(
              'peer flex-1 min-w-0 bg-transparent border-none outline-none px-1',
              'text-foreground placeholder:text-transparent',
              'focus:ring-0 focus:outline-none',
              valueTextClass,
            )}
            {...props}
          />
          {suffix !== undefined && (
            <span className={cn('text-muted-foreground whitespace-nowrap', valueTextClass)}>
              {suffix}
            </span>
          )}
        </div>
        <label
          htmlFor={inputId}
          className={cn(
            'pointer-events-none absolute z-10 origin-[0] text-base text-muted-foreground transition-all',
            // Resting position: aligned to the value baseline inside the field.
            labelTopClass,
            'start-3',
            // Lifted position when the input has a value or is focused.
            'scale-75 -translate-y-3',
            'peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0',
            'peer-focus:scale-75 peer-focus:-translate-y-3 peer-focus:text-foreground',
          )}
        >
          {label}
        </label>
      </div>
    );
  },
);
FloatingInput.displayName = 'FloatingInput';

export { FloatingInput };
